import React, { useState, useEffect } from "react";
import "./robotSettingsNew.scss";
import PlantHeader from "../plantHeader/plantHeader";
import Menu from "../menu/menu";
import Search from "../search/search";
import download from "./download-icon.svg";
import upload from "./upload-icon.svg";
import Loader from "../loader/loader";
import EditRobotSettings from "../editRobotSettings/editRobotSettings";
import { useNavigate } from "react-router-dom";
import { LOCAL_STORAGE_USER_KEY } from "../../consts";
import ApiHandler from "../api";
import { Tooltip } from "react-tooltip";
import { getCurrentProject } from "../../utils/projectUtils";

const RobotSettingsNew = () => {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [robotSettings, setRobotSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditRobotSettings, setShowEditRobotSettings] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [sortParam, setSortParam] = useState("rowNumber");
  const [sortDirection, setSortDirection] = useState(1);
  const [user, setUser] = useState(null);

  const apiHandler = new ApiHandler();

  useEffect(() => {
    const storedProject = getCurrentProject();
    if (!storedProject) {
      navigate("/projects");
      return;
    }
    setProject(storedProject);
    const user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_KEY));
    setUser(user);
  }, [navigate]);

  useEffect(() => {
    if (project?._id) {
      fetchRobotSettings();
    }
  }, [project]);

  const fetchRobotSettings = async (search = "") => {
    setIsLoading(true);
    try {
      const response = await apiHandler.getRobotSettings(project._id, search);
      setRobotSettings(response || []);
    } catch (error) {
      console.error("Error fetching robot settings:", error);
      alert("Failed to load robot settings");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      setIsLoading(true);
      const response = await apiHandler.exportRobotSettings(project._id);
      const element = document.createElement("a");
      const file = new Blob([response.data], { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${project.projectName} robot settings.csv`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      alert("Failed to download CSV");
    } finally {
      setIsLoading(false);
    }
  };

  const searchClicked = (search) => {
    fetchRobotSettings(search);
  };

  const fileUploadClick = () => {
    document.getElementById("fileUpload").click();
  };

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      !window.confirm(
        `The file ${file.name} will overwrite all the existing robot settings. Are you sure you want to continue?`
      )
    ) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("projectId", project._id);

    try {
      setIsLoading(true);
      await apiHandler.importRobotSettings(formData);
      alert("File uploaded successfully");
      fetchRobotSettings();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    } finally {
      setIsLoading(false);
      event.target.value = "";
    }
  };

  const editRobotSettings = (selectedRobot) => {
    setSelectedRobot(selectedRobot);
    setShowEditRobotSettings(true);
  };

  const closeEditRobotSettings = (refresh = true) => {
    setShowEditRobotSettings(false);
    setSelectedRobot(null);
    // Always refresh data when closing modal to ensure we have the latest state
    if (refresh) {
      fetchRobotSettings();
    }
  };

  const onRobotSettingsUpdated = () => {
    fetchRobotSettings();
  };

  const getBooleanValue = (val) =>
    val === "" || val === undefined
      ? ""
      : val === true || val === "yes"
      ? "Yes"
      : val === false || val === "no"
      ? "No"
      : val;

  const getHeaderTH = (paramName, text, className = "", type = "") => {
    const currentSort = sortParam === paramName;
    const direction = currentSort && sortDirection === 1 ? "asc" : "desc";

    return (
      <th
        key={paramName}
        className={`${className} ${currentSort ? direction : ""}`}
        onClick={() => handleSort(paramName)}
      >
        {text}
      </th>
    );
  };

  const handleSort = (paramName) => {
    if (sortParam === paramName) {
      setSortDirection(sortDirection * -1);
    } else {
      setSortParam(paramName);
      setSortDirection(1);
    }
  };

  if (!project) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page">
      {isLoading && <Loader />}

      {showEditRobotSettings && (
        <EditRobotSettings
          closeModal={closeEditRobotSettings}
          robot={selectedRobot}
          onRobotUpdated={onRobotSettingsUpdated}
          user={user}
        />
      )}

      <div className="menu-wrapper">
        <Menu />
      </div>
      <div className="content">
        <PlantHeader
          title={"Robot Settings"}
          secondaryText={project.projectName}
        />
        <div className="robot-settings-page">
          <div className="top">
            <Search
              onSearch={searchClicked}
              placeholder="search by robot number"
            />
            {user.role === "admin" && (
              <div className="buttons">
                <div className="button">
                  <img src={download} alt="download" />
                  <span className="text" onClick={downloadCsv}>
                    EXPORT
                  </span>
                </div>

                <div className="button" onClick={fileUploadClick}>
                  <img src={upload} alt="upload" />
                  <span className="text">UPLOAD CSV</span>
                  <input
                    id="fileUpload"
                    type="file"
                    accept=".csv"
                    onChange={onFileChange}
                    style={{ display: "none" }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {getHeaderTH("robot", "Robot")}
                  {getHeaderTH(
                    "driveMotorType",
                    "Drive Motor Type",
                    "wide-column"
                  )}
                  {getHeaderTH(
                    "cleaningMotorType",
                    "Cleaning Motor Type",
                    "wide-column"
                  )}
                  {getHeaderTH("driveSpeedTransit", "Drive Speed Transit")}
                  {getHeaderTH("driveSpeedCleaning", "Drive Speed Cleaning")}
                  {getHeaderTH("rotorSpeedTransit", "Rotor Speed Transit")}
                  {getHeaderTH("rotorSpeedCleaning", "Rotor Speed Cleaning")}
                  {getHeaderTH("stopDistanceTransit", "Stop Distance Transit")}
                  {getHeaderTH(
                    "stopDistanceCleaning",
                    "Stop Distance Cleaning"
                  )}
                  {getHeaderTH(
                    "halfSecCurrentThresholdDriveMotor",
                    "0.5s Current Threshold Drive"
                  )}
                  {getHeaderTH(
                    "halfSecCurrentThresholdCleaningMotor",
                    "0.5s Current Threshold Clean"
                  )}
                  {getHeaderTH(
                    "twoSecCurrentThresholdDriveMotor",
                    "2s Current Threshold Drive"
                  )}
                  {getHeaderTH(
                    "twoSecCurrentThresholdCleaningMotor",
                    "2s Current Threshold Clean"
                  )}
                  {getHeaderTH(
                    "rpmTargetRatioDriveMotor",
                    "RPM Target Ratio Drive"
                  )}
                  {getHeaderTH(
                    "rpmTargetRatioCleaningMotor",
                    "RPM Target Ratio Clean"
                  )}
                  {getHeaderTH(
                    "cleanMotorSpeedRatioWhileTestingPERC",
                    "Clean Motor Speed Ratio PERC"
                  )}
                  {getHeaderTH(
                    "batteryThresholdHighLoad",
                    "Battery Threshold High Load"
                  )}
                  {getHeaderTH(
                    "batteryThresholdLowLoad",
                    "Battery Threshold Low Load"
                  )}
                  {getHeaderTH(
                    "reserveBatteryThresholdAh",
                    "Reserve Battery Threshold Ah"
                  )}
                  {getHeaderTH(
                    "batteryVoltageThreshold",
                    "Battery Voltage Threshold"
                  )}
                  {getHeaderTH("batterySize", "Battery Size [Ah]")}
                  {getHeaderTH(
                    "rowEdgesDoubleCleaningDistance",
                    "Row Edges Double Clean Distance"
                  )}
                  {getHeaderTH("fastDrying", "Fast Drying", "", "boolean")}
                  {getHeaderTH(
                    "chargeFixDuringBatteryTest",
                    "Charge Fix During Battery Test",
                    "",
                    "boolean"
                  )}
                  {getHeaderTH(
                    "jammedRobotFixOnTable",
                    "Jammed Robot Fix On Table",
                    "",
                    "boolean"
                  )}
                  {getHeaderTH(
                    "operationMode",
                    "Operation Mode",
                    "wide-column"
                  )}
                </tr>
              </thead>
              <tbody>
                {robotSettings.map((robotSetting, index) => (
                  <tr
                    key={index}
                    onClick={() => editRobotSettings(robotSetting)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      {robotSetting.robotName}
                      {robotSetting.rpi ? ` (${robotSetting.rpi})` : ""}
                    </td>
                    <td
                      className="wide-column"
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content={
                        robotSetting.settings?.driveMotorType || ""
                      }
                    >
                      {robotSetting.settings?.driveMotorType || ""}
                    </td>
                    <td
                      className="wide-column"
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content={
                        robotSetting.settings?.cleaningMotorType || ""
                      }
                    >
                      {robotSetting.settings?.cleaningMotorType || ""}
                    </td>
                    <td>{robotSetting.settings?.driveSpeedTransit || ""}</td>
                    <td>{robotSetting.settings?.driveSpeedCleaning || ""}</td>
                    <td>{robotSetting.settings?.rotorSpeedTransit || ""}</td>
                    <td>{robotSetting.settings?.rotorSpeedCleaning || ""}</td>
                    <td>{robotSetting.settings?.stopDistanceTransit || ""}</td>
                    <td>{robotSetting.settings?.stopDistanceCleaning || ""}</td>
                    <td>
                      {robotSetting.settings
                        ?.halfSecCurrentThresholdDriveMotor || ""}
                    </td>
                    <td>
                      {robotSetting.settings
                        ?.halfSecCurrentThresholdCleaningMotor || ""}
                    </td>
                    <td>
                      {robotSetting.settings
                        ?.twoSecCurrentThresholdDriveMotor || ""}
                    </td>
                    <td>
                      {robotSetting.settings
                        ?.twoSecCurrentThresholdCleaningMotor || ""}
                    </td>
                    <td>
                      {robotSetting.settings?.rpmTargetRatioDriveMotor || ""}
                    </td>
                    <td>
                      {robotSetting.settings?.rpmTargetRatioCleaningMotor || ""}
                    </td>
                    <td>
                      {robotSetting.settings
                        ?.cleanMotorSpeedRatioWhileTestingPERC || ""}
                    </td>
                    <td>
                      {robotSetting.settings?.batteryThresholdHighLoad || ""}
                    </td>
                    <td>
                      {robotSetting.settings?.batteryThresholdLowLoad || ""}
                    </td>
                    <td>
                      {robotSetting.settings?.reserveBatteryThresholdAh || ""}
                    </td>
                    <td>
                      {robotSetting.settings?.batteryVoltageThreshold || ""}
                    </td>
                    <td>{robotSetting.settings?.batterySize || ""}</td>
                    <td>
                      {robotSetting.settings?.rowEdgesDoubleCleaningDistance ||
                        ""}
                    </td>
                    <td className="boolean">
                      {getBooleanValue(robotSetting.settings?.fastDrying)}
                    </td>
                    <td className="boolean">
                      {getBooleanValue(
                        robotSetting.settings?.chargeFixDuringBatteryTest
                      )}
                    </td>
                    <td className="boolean">
                      {getBooleanValue(
                        robotSetting.settings?.jammedRobotFixOnTable
                      )}
                    </td>
                    <td className="wide-column">
                      {robotSetting.settings?.operationMode || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Tooltip id="my-tooltip" />
    </div>
  );
};

export default RobotSettingsNew;
